import React from "react";
import { Box, Typography, IconButton, Chip, Button, Skeleton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Styles from "./MasterHistory.module.scss";
import {
  actionMeta,
  avatarColor,
  describeValue,
  flattenSnapshot,
  humanizeField,
  initialsOf,
  isEmptyValue,
  sameValue,
} from "./historyFormat";

export const outlinedButtonSx = {
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 500,
  borderRadius: "8px",
  borderColor: "#D0D5DD",
  color: "#344054",
  "&:hover": { borderColor: "#1E3A5F", backgroundColor: "#F2F4F7" },
};

export const navySwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#1E3A5F" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#1E3A5F" },
};

export const switchLabelSx = {
  mr: 0.5,
  ml: 0,
  "& .MuiFormControlLabel-label": { fontSize: "12px", color: "#344054", whiteSpace: "nowrap" },
};

/** Same header treatment as the app's Master_Card and the job-history dialog. */
export const DialogHeader = ({ titleId, title, subtitle, actions, onClose, icon }) => (
  <Box
    sx={{
      padding: "10px 18px",
      borderBottom: "1px solid #eaecf0",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1.5,
      flexShrink: 0,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
        backgroundColor: "#1E3A5F",
        color: "#ffffff",
      }}
    >
      {icon || <HistoryIcon sx={{ fontSize: 20 }} />}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        id={titleId}
        sx={{ color: "#101828", fontWeight: 500, fontSize: "16px", fontFamily: "inter" }}
      >
        {title}
      </Typography>
      <Typography variant="body2" noWrap sx={{ color: "#667085", fontSize: "12.5px" }}>
        {subtitle}
      </Typography>
    </Box>
    {actions && (
      // Drops under the title on phones so the title keeps its room.
      <Box
        sx={{
          order: { xs: 3, sm: 0 },
          width: { xs: "100%", sm: "auto" },
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {actions}
      </Box>
    )}
    <IconButton size="small" onClick={onClose} aria-label="Close">
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

export const SummaryStrip = ({ items }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: 2,
      p: 1.75,
      mb: 2.5,
      border: "1px solid #eaecf0",
      borderRadius: "8px",
      backgroundColor: "#fcfcfd",
    }}
  >
    {items.map((item) => (
      <Box key={item.label} sx={{ minWidth: 120 }}>
        <Typography sx={{ fontSize: "11px", color: "#667085", mb: 0.25 }}>{item.label}</Typography>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: item.color || "#101828" }}>
          {String(item.value)}
        </Typography>
      </Box>
    ))}
  </Box>
);

export const ActionChip = ({ action }) => {
  const meta = actionMeta(action);
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        height: 20,
        fontSize: "11px",
        fontWeight: 600,
        color: meta.color,
        backgroundColor: meta.bg,
      }}
    />
  );
};

export const VersionPill = ({ version }) => <span className={Styles.versionPill}>v{version}</span>;

export const Person = ({ name }) => {
  const label = name || "Unknown";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 22,
          height: 22,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          backgroundColor: avatarColor(label),
          color: "#ffffff",
          fontSize: "9.5px",
          fontWeight: 600,
        }}
      >
        {initialsOf(name)}
      </Box>
      <Typography noWrap sx={{ fontSize: "12.5px", fontWeight: 500, color: "#344054" }}>
        {label}
      </Typography>
    </Box>
  );
};

/** `plain` drops chips so the value can sit inside a tinted old/new pill. */
export const FormattedValue = ({ field, value, plain = false }) => {
  const shown = describeValue(field, value);

  if (shown.kind === "empty") return <span style={{ color: "#98A2B3" }}>—</span>;
  if (shown.kind === "redacted") {
    return <span style={{ color: "#98A2B3", fontStyle: "italic" }}>{shown.text}</span>;
  }
  if (shown.kind === "bool" && !plain) {
    return (
      <Chip
        label={shown.text}
        size="small"
        sx={{
          height: 20,
          fontSize: "11px",
          fontWeight: 600,
          color: shown.positive ? "#027A48" : "#475467",
          backgroundColor: shown.positive ? "#ECFDF3" : "#F2F4F7",
        }}
      />
    );
  }
  if (shown.kind === "id") {
    return (
      <span className={Styles.mono} title={shown.title}>
        {shown.text}
      </span>
    );
  }
  if (shown.kind === "json") return <span className={Styles.mono}>{shown.text}</span>;
  return <span style={{ overflowWrap: "anywhere" }}>{shown.text}</span>;
};

const CompareBadge = ({ field, to, currentFlat }) => {
  if (sameValue(to, currentFlat[field])) {
    return (
      <Chip
        label="Still current"
        size="small"
        sx={{
          height: 18,
          fontSize: "10.5px",
          fontWeight: 600,
          color: "#027A48",
          backgroundColor: "#ECFDF3",
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.75,
        py: "1px",
        border: "1px solid #FEDF89",
        borderRadius: "6px",
        backgroundColor: "#FFFAEB",
        color: "#B54708",
        fontSize: "11.5px",
      }}
    >
      Now: <FormattedValue field={field} value={currentFlat[field]} plain />
    </Box>
  );
};

/** `Field   old -> new` rows. `currentFlat` adds a "still current / now" badge to each. */
export const ChangeList = ({ changes, currentFlat }) => (
  <Box role="list" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    {changes.map((change, index) => (
      <Box
        key={`${change.field}-${index}`}
        role="listitem"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "170px 1fr" },
          columnGap: 1.5,
          rowGap: 0.25,
          alignItems: "start",
        }}
      >
        <Typography sx={{ fontSize: "12.5px", fontWeight: 500, color: "#344054", lineHeight: "26px" }}>
          {humanizeField(change.field)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, minHeight: 26 }}>
          <span
            className={`${Styles.oldValue} ${isEmptyValue(change.from) ? Styles.emptyValue : ""}`}
          >
            <FormattedValue field={change.field} value={change.from} plain />
          </span>
          <ArrowForwardIcon sx={{ fontSize: 14, color: "#98A2B3" }} />
          <span
            className={`${Styles.newValue} ${isEmptyValue(change.to) ? Styles.emptyValue : ""}`}
          >
            <FormattedValue field={change.field} value={change.to} plain />
          </span>
          {currentFlat && (
            <CompareBadge field={change.field} to={change.to} currentFlat={currentFlat} />
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

/** Key/value view of a whole record; with `currentFlat`, fields that differ are marked amber. */
export const SnapshotGrid = ({ snapshot, currentFlat }) => {
  const flat = flattenSnapshot(snapshot);
  const fields = currentFlat
    ? [...Object.keys(flat), ...Object.keys(currentFlat).filter((field) => !(field in flat))]
    : Object.keys(flat);

  if (!fields.length) {
    return (
      <Typography sx={{ fontSize: "12.5px", color: "#667085" }}>No values were recorded.</Typography>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: 320,
        overflowY: "auto",
        border: "1px solid #eaecf0",
        borderRadius: "8px",
        backgroundColor: "#ffffff",
      }}
    >
      {fields.map((field, index) => {
        const differs = currentFlat && !sameValue(flat[field], currentFlat[field]);
        return (
          <Box
            key={field}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
              columnGap: 1.5,
              rowGap: 0.25,
              px: 1.5,
              py: 0.75,
              borderLeft: `3px solid ${differs ? "#F79009" : "transparent"}`,
              borderBottom: index < fields.length - 1 ? "1px dashed #eaecf0" : "none",
              backgroundColor: differs ? "#FFFAEB" : "transparent",
            }}
          >
            <Typography sx={{ fontSize: "12.5px", fontWeight: 500, color: "#667085" }}>
              {humanizeField(field)}
            </Typography>
            <Box sx={{ fontSize: "12.5px", color: "#101828", minWidth: 0 }}>
              <FormattedValue field={field} value={flat[field]} />
              {differs && (
                <Typography component="div" sx={{ mt: 0.25, fontSize: "11.5px", color: "#B54708" }}>
                  Current: <FormattedValue field={field} value={currentFlat[field]} plain />
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export const HistorySkeleton = ({ rows = 3 }) => (
  <Box aria-busy="true" aria-label="Loading history">
    <Skeleton variant="rounded" height={68} sx={{ mb: 2.5, borderRadius: "8px" }} />
    {Array.from({ length: rows }).map((_, index) => (
      <Box key={index} sx={{ display: "flex", gap: 1.5, mb: 1.75 }}>
        <Skeleton variant="circular" width={10} height={10} sx={{ mt: "17px", mx: "2px" }} />
        <Skeleton variant="rounded" height={92} sx={{ flex: 1, borderRadius: "8px" }} />
      </Box>
    ))}
  </Box>
);

export const HistoryEmpty = ({ icon, title, text, action }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.75,
      px: 2,
      py: 5,
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        mb: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        backgroundColor: "#F2F4F7",
        color: "#1E3A5F",
      }}
    >
      {icon || <HistoryIcon />}
    </Box>
    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#101828" }}>{title}</Typography>
    {text && (
      <Typography sx={{ maxWidth: 440, fontSize: "12.5px", color: "#667085" }}>{text}</Typography>
    )}
    {action}
  </Box>
);

export const HistoryError = ({ message, onRetry }) => (
  <Box
    role="alert"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      p: 1.5,
      border: "1px solid #f3d6d6",
      borderRadius: "8px",
      backgroundColor: "#fdf7f7",
    }}
  >
    <ErrorOutlineIcon sx={{ fontSize: 20, color: "#b3261e" }} />
    <Typography sx={{ flex: 1, fontSize: "12.5px", color: "#b3261e" }}>
      {message || "Could not load history"}
    </Typography>
    {onRetry && (
      <Button size="small" variant="outlined" onClick={onRetry} sx={outlinedButtonSx}>
        Retry
      </Button>
    )}
  </Box>
);

const NOTE_TONES = {
  info: { icon: InfoOutlinedIcon, color: "#175CD3", border: "#B2DDFF", bg: "#F5FAFF" },
  warn: { icon: WarningAmberIcon, color: "#B54708", border: "#FEDF89", bg: "#FFFAEB" },
};

export const Note = ({ tone = "info", children }) => {
  const { icon: Icon, color, border, bg } = NOTE_TONES[tone];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        p: 1.5,
        mb: 2,
        border: `1px solid ${border}`,
        borderRadius: "8px",
        backgroundColor: bg,
      }}
    >
      <Icon sx={{ fontSize: 18, mt: "1px", color }} />
      <Typography sx={{ fontSize: "12.5px", color: "#344054" }}>{children}</Typography>
    </Box>
  );
};
